import React, { useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Beaker, ExternalLink, Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * 🆕 22/07/2026 — Canary test end-to-end del checkout Stripe.
 * Usato dall'admin dopo ogni redeploy per verificare che la catena
 * STRIPE_API_KEY → settings → gross-up → checkout sia integra.
 */
export default function StripeCanaryTest() {
  const [netAmount, setNetAmount] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const runTest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/stripe-canary-test", { net_amount: Number(netAmount) });
      setResult(data);
      toast.success("Sessione Stripe creata · apri il link per completare il test");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="stripe-canary-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-md bg-indigo-600 text-white flex items-center justify-center">
          <Beaker className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Stripe canary test</h1>
          <p className="text-sm text-slate-500 mt-0.5">Verifica end-to-end della catena Stripe. Usa un importo simbolico (default € 1,00). Applica il gross-up come nel flow reale.</p>
        </div>
      </div>

      {sessionId && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-2 text-sm" data-testid="canary-success">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-900">Pagamento test completato con successo.</p>
            <p className="text-xs text-emerald-800 mt-0.5">Session ID: <span className="font-mono">{sessionId}</span></p>
            <p className="text-xs text-emerald-800 mt-0.5">Verifica in Stripe Dashboard che l&apos;importo netto sia esattamente il &quot;net_amount&quot; scelto (al netto del fee Stripe).</p>
          </div>
        </div>
      )}
      {cancelled && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md flex items-start gap-2 text-sm">
          <XCircle className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-slate-700">Test annullato dall&apos;utente.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div>
          <Label htmlFor="net-amount" className="text-sm">Importo netto (agenzia)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="net-amount"
              type="number"
              step="0.10"
              min="0.10"
              max="10"
              value={netAmount}
              onChange={(e) => setNetAmount(e.target.value)}
              className="max-w-[180px]"
              data-testid="canary-net-input"
            />
            <Button
              onClick={runTest}
              disabled={loading || !netAmount || Number(netAmount) <= 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="canary-run-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Beaker className="h-4 w-4 mr-2" />}
              Genera checkout test
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">Il minimo Stripe è € 0,50. Se applichi il gross-up, il totale può salire.</p>
        </div>

        {result && (
          <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="canary-result">
            <div className="grid grid-cols-3 gap-3 text-sm tabular-nums">
              <div className="p-3 rounded bg-slate-50 border border-slate-200">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">Netto agenzia</p>
                <p className="text-lg font-semibold text-slate-900 mt-0.5">{formatCurrency(result.net_amount)}</p>
              </div>
              <div className="p-3 rounded bg-amber-50 border border-amber-200">
                <p className="text-[10px] uppercase text-amber-700 font-semibold">Ricarico fee</p>
                <p className="text-lg font-semibold text-amber-800 mt-0.5">+ {formatCurrency(result.fee_amount)}</p>
              </div>
              <div className="p-3 rounded bg-indigo-50 border border-indigo-300">
                <p className="text-[10px] uppercase text-indigo-700 font-semibold">Cliente paga</p>
                <p className="text-lg font-semibold text-indigo-800 mt-0.5">{formatCurrency(result.gross_amount)}</p>
              </div>
            </div>
            <div className="text-xs text-slate-500">
              <p>Fee configurate: <strong>{result.fee_percent}%</strong> + <strong>{formatCurrency(result.fee_fixed)}</strong> · Charge to client: <strong>{result.charge_fee_to_client ? "ON" : "OFF"}</strong></p>
              <p className="mt-0.5">Session ID: <span className="font-mono text-[11px]">{result.session_id}</span></p>
            </div>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
              data-testid="canary-open-checkout"
            >
              <ExternalLink className="h-4 w-4" />
              Apri Stripe Checkout
            </a>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600 space-y-1">
        <p className="font-semibold text-slate-700">🧪 Checklist di verifica post-deploy:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>La sessione viene creata senza errore (Stripe API key valida).</li>
          <li>Il gross-up rispecchia le impostazioni (%/fisso) di Config. Pagamenti.</li>
          <li>Dopo il pagamento di test, torni qui con il badge verde &quot;successo&quot;.</li>
          <li>In Stripe Dashboard verifichi che il &quot;netto&quot; incassato = <code>net_amount</code>.</li>
        </ul>
      </div>
    </div>
  );
}
